import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { installUpdate } from '../../platform/installer';
import { dismiss, isDismissed, newerRelease, type Release } from '../../platform/updates';

/**
 * "There is a newer version." Shown in the Android application only, once per
 * release, after signing in.
 *
 * Downloading is a plain link. The address is on the server rather than in the
 * WebView, so Capacitor hands it to the system browser, which downloads the
 * file and offers to install it — the same path as any other APK a phone is
 * given, with the phone's own warning about where it came from. Nothing here
 * installs anything quietly, and nothing should.
 */
export function UpdateNotice() {
  const { t } = useTranslation();
  const [release, setRelease] = useState<Release | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void newerRelease().then((found) => {
      if (cancelled || !found || isDismissed(found.version)) return;
      setRelease(found);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!release) return null;

  return (
    <div className="shell__update" role="status">
      <span>
        {t('update.available', { version: release.version })}
        {error && ` · ${t(`update.errors.${error}`, t('update.errors.unknown'))}`}
      </span>

      <button
        type="button"
        className="shell__update-get"
        disabled={busy}
        onClick={() => {
          setError(null);
          setBusy(true);
          setProgress(0);
          void installUpdate(release.url, setProgress)
            .catch((exc: unknown) => setError((exc as Error)?.message || 'unknown'))
            .finally(() => setBusy(false));
        }}
      >
        {busy
          ? t('update.downloading', { percent: Math.round(progress * 100) })
          : t('update.download')}
      </button>

      <button
        type="button"
        disabled={busy}
        onClick={() => {
          dismiss(release.version);
          setRelease(null);
        }}
      >
        {t('update.later')}
      </button>
    </div>
  );
}
